import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams({
        Site: "http://localhost:8080/", // or your site name
        Name: formState.name,
        "Email Address": formState.email,
        Phone: formState.phone,
        Message: formState.message,
        // Message: `Phone: ${formState.phone}\n\nMessage: ${formState.message}`,
      });
      const response = await fetch(`https://n8n.server.nrupesh.com/webhook/form-submition?${body.toString()}`);
      if (!response.ok) throw new Error("Failed to submit form");
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you as soon as possible.",
      });
      setFormState({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        title: "Submission failed",
        description: "There was an error sending your message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-white dark:bg-gray-900">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-md bg-seafarer-orange/10 mr-4">
                <MapPin className="h-6 w-6 text-seafarer-orange" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Our Location</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  CBD, 5th Road<br />
                  Walvis Bay, ERF 25<br />
                  Namibia
                </p>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  <strong>Postal Address:</strong><br />
                  P.O.Box 4818<br />
                  Walvis Bay<br />
                  Namibia
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-md bg-seafarer-purple/10 mr-4">
                <Phone className="h-6 w-6 text-seafarer-purple" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Phone</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  <a href="tel:+264-64-221-787" className="hover:text-seafarer-orange transition-colors">
                    +264 64 221 787 / +26481 143 7833
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-md bg-seafarer-blue/10 mr-4">
                <Mail className="h-6 w-6 text-seafarer-blue" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Email</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  <a href="mailto:octoberseafarersgreenvally@gmail.com" className="hover:text-seafarer-orange transition-colors">
                    octoberseafarersgreenvally@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
          
          <div className="seafarer-card p-8 animate-fade-in">
            <h3 className="text-2xl font-semibold mb-6">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Full Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-1">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  rows={4}
                  required
                />
              </div>
              
              <Button type="submit" className="seafarer-btn-primary w-full" disabled={isSubmitting}>
                <Send className="h-4 w-4 mr-2" /> {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
